import React, {FC, useMemo, useState} from "react";

// antd:
import { Typography } from "antd";
import Prism from "prismjs";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

import DivE from "./DivE.tsx";
import SpanE from "./SpanE.tsx";
import BrE from "./BrE.tsx";

type componentProps = {
  id?:string, key?:string,
  style?:React.CSSProperties | CSS.Properties,
  color?:string,
  backgroundColor?:string,
  className?:string,
  title?:string,
  title_ru?:string,
  title_en?:string,
  title_tr?:string,
  tooltip?:string,
  tooltip_ru?:string,
  tooltip_en?:string,
  tooltip_tr?:string,
  children?:React.ReactNode, // | React.ReactElement<any> | React.CSSProperties
  blockType?:"Text" | "Title" | "Paragraph",
  align?:string,
  type?:string,
  format?:string,
  level?:number,
  size?:string,
  disabled?:boolean | "true" | "false",
  mark?:boolean | "true" | "false",
  code?:boolean | "true" | "false",
  keyboard?:boolean | "true" | "false",
  underline?:boolean | "true" | "false",
  delete?:boolean | "true" | "false",
  strong?:boolean | "true" | "false",
  italic?:boolean | "true" | "false",
  copyable?:boolean | "true" | "false",
  editable?:boolean | "true" | "false",
  ellipsis?:boolean | string | {tooltip?:string, rows?:number, expandable?:boolean, symbol?:string, suffix?:string},
  visible?:boolean | "true" | "false", hidden?:boolean | "true" | "false",
  language?:"javascript" | "html" | "css" | "xml"
} & {[key:string]:any}; // string | boolean | number | React.ReactNode

type TexType = {
  blockType:"Text" | "Title" | "Paragraph",
  type?:string,
  format?:string,
  level?:number,
  size?:string,
  disabled:boolean,
  mark:boolean,
  code:boolean,
  keyboard:boolean,
  underline:boolean,
  delete:boolean,
  strong:boolean,
  italic:boolean,
  copyable:boolean,
  editable:boolean,
  ellipsis:boolean | {tooltip?:string, rows?:number, expandable?:boolean, symbol?:string, suffix?:string},
  language?:"javascript" | "html" | "css" | "xml"
}

const {rz, $} = window;

const TextE:FC<componentProps> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [theme, setTheme] = useState($("html").attr("data-theme"));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lng, setLng] = useState($("html").attr("lang"));
  const rlng = (lng === "en" ? "ru" : "en");

  const ttype = useMemo<TexType>(() => {
    const typ:TexType = {
      blockType: "Text", level: undefined, type: undefined, disabled: false, mark: false, code: false, keyboard: false,
      underline: false, delete: false, strong: false, italic: false, editable: false, copyable: false,
      ellipsis: false
    };
    (props.type || props.format || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["h1", "h2", "h3", "h4", "h5"].indexOf(tt)) { typ.level = rz.strSearchNum(tt, 1); typ.blockType = "Title" }
      else if (~["default", "secondary", "success", "warning", "danger"].indexOf(tt)) { typ.type = tt; typ.blockType = "Text" }
      else if (tt === "paragraph") { typ.blockType = "Paragraph" }
      else if (tt === "disabled") { typ.disabled = true }
      else if (tt === "mark") { typ.mark = true }
      else if (tt === "code") { typ.code = true }
      else if (tt === "keyboard") { typ.keyboard = true }
      else if (tt === "underline") { typ.underline = true }
      else if (tt === "delete") { typ.delete = true }
      else if (~["strong", "bold"].indexOf(tt)) { typ.strong = true }
      else if (tt === "italic") { typ.italic = true }
      else if (~["editable", "edit"].indexOf(tt)) { typ.editable = true }
      else if (~["copyable", "copy"].indexOf(tt)) { typ.copyable = true }
      // @ts-ignore
      else if (~["javascript", "html", "css", "xml"].indexOf(tt)) { typ.code = true; typ.language = tt }
      else if ((/\d+(\.\d+)?em$/ig).test(tt) || (/\d+(\.\d+)?rem$/ig).test(tt) || (/\d+(\.\d+)?px$/ig).test(tt) || (/\d+(\.\d+)?pt$/ig).test(tt)) { typ.size = tt }
    });

    if (props.editable === true || props.editable === "true") { typ.editable = true }
    if (props.copyable === true || props.copyable === "true") { typ.copyable = true }
    if (props.size) { typ.size = props.size }
    if (props.ellipsis) {
      if (props.ellipsis === true || props.ellipsis === "true") { typ.ellipsis = {expandable: true, rows: 2} }
      // @ts-ignore
      else if (rz.isString(props.ellipsis) && props.ellipsis.startsWith("{") && props.ellipsis.endsWith("}")) {
      // @ts-ignore
        const jsnb = (rz.strReplaceAll(props.ellipsis, "'", "\"")).trim(); let jsn; try { jsn = JSON.parse(jsnb) } catch (err) { console.log(err) }
        if (jsn) { typ.ellipsis = jsn } else { typ.ellipsis = {expandable: true, rows: 2} }
      }
    }
    if (props.language) { typ.code = true; typ.language = props.language }
    return typ;
  }, [props.copyable, props.editable, props.ellipsis, props.format, props.language, props.size, props.type]);

  const styl = useMemo<React.CSSProperties | CSS.Properties>(() => {
    const est:React.CSSProperties | CSS.Properties = {...props.style};
    let clr:string; const allc = (props.color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (props.backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [props, theme]);

  const title = useMemo<string | undefined>(() => {
    let ttl = (props.title || props["title_" + lng] || props["title_" + rlng] || "");
    if (rz.isString(ttl)) {
      while (ttl.startsWith("\n")) { ttl = ttl.slice(1) }
      while (ttl.endsWith("\n")) { ttl = ttl.slice(0, -1) }
      if (~ttl.indexOf("\n")) {
        const aa = ttl.split("\n");
        if (ttype.code) {
          ttl = React.createElement(DivE, {key: "dive-wrapper-" + (props.id || "")}, aa.map((ii, i) => {
            const bb = ii.replace((/^(\s+)/g), (cc) => (cc.replace((/(\s{2,2})/g), " ")));
            const html = Prism.highlight((bb || ""), Prism.languages.javascript, ttype.language || "javascript");
            return React.createElement(SpanE, {key: "span-wrapper-" + (i + 1) + "-" + (props.id || "")}, [
              // eslint-disable-next-line react/no-danger-with-children
              React.createElement(SpanE, {key: "span-content-" + (i + 1) + "-" + (props.id || ""), dangerouslySetInnerHTML: {__html: html}}),
              React.createElement(BrE, {key: "br-" + (i + 1) + "-" + (props.id || "")})
            ])
          }));
        }
        else {
          ttl = React.createElement(DivE, {key: "dive-wrapper-" + (props.id || "")}, aa.map((ii, i) => {
            const bb = ii.replace((/^(\s+)/g), (cc) => (cc.replace((/(\s{2,2})/g), " ")));
            return React.createElement(SpanE, {key: "span-wrapper-" + (i + 1) + "-" + (props.id || "")}, [
              React.createElement(SpanE, {key: "span-content-" + (i + 1) + "-" + (props.id || "")}, bb),
              React.createElement(BrE, {key: "br-" + (i + 1) + "-" + (props.id || "")})
            ])
          }));
        }
      }
    }
    return ttl;
  }, [props, lng, rlng, ttype.code, ttype.language]);

  const tooltip = useMemo<string | undefined>(() => (props.tooltip || props["tooltip_" + lng] || props["tooltip_" + rlng] || ""), [props, lng, rlng]);

  const flex = useMemo<string>(() => {
    const flx = ["uk-flex"];
    (props.align || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if ((/^(left|center|right|between|around|stretch|top|middle|bottom|row|column|wrap|nowrap|first|last|none|auto|\d+)/igm).test(tt)) { flx.push("uk-flex-" + tt) }
    });
    return flx.join(" ");
  }, [props.align]);

  if (ttype.code && !rz.isString(title)) {
    return (
      <Typography.Text code={true} id={props?.id} >
        {title}
      </Typography.Text>
    );
  }

  const Txt = React.createElement(Typography[ttype.blockType], {
    id: props?.id,
    // @ts-ignore
    // eslint-disable-next-line no-unneeded-ternary
    style: styl, className: props?.className, "uk-tooltip": (tooltip ? tooltip : undefined),
    // @ts-ignore
    level: ttype.level, type: ttype.type, mark: ttype.mark, code: ttype.code, keyboard: ttype.keyboard,
    // @ts-ignore
    underline: ttype.underline, delete: ttype.delete, strong: ttype.strong,
    // @ts-ignore
    copyable: ttype.copyable,
    // @ts-ignore
    editable: ttype.editable,
    // @ts-ignore
    ellipsis: ttype.ellipsis,
    // @ts-ignore
    disabled: ttype.disabled,
    hidden: (props?.visible === false || props?.visible === "false" || props?.hidden === true || props?.hidden === "true")
  }, (title || props.children || ""));

  if (!props.align) { return Txt }

  return (<DivE key={props.key || props.id || "dive-wrapper"} className={flex}>{Txt}</DivE>);
};

export default TextE;
